import React, { useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import { Button, Input, Label } from "reactstrap";
import {
  ApprovalSettings,
  PaymentDetails,
  getOrderApprovalSettings,
  saveOrderApprovalSettings,
} from "../../../services/orderApprovalApi";
import { isStudioStaff } from "./approvalStatus";
import "../../gi-registry/gi-registry.scss";
import "../orders.scss";

type PayForm = Required<{ [K in keyof PaymentDetails]: string }>;

const EMPTY_PAY: PayForm = { upiId: "", payeeName: "", bankName: "", accountName: "", accountNumber: "", ifsc: "", note: "" };

const PAY_FIELDS: Array<{ key: keyof PayForm; label: string; placeholder?: string; max: number }> = [
  { key: "upiId", label: "UPI ID", placeholder: "studio@okhdfcbank", max: 100 },
  { key: "payeeName", label: "Payee name (as on UPI)", max: 100 },
  { key: "bankName", label: "Bank", max: 100 },
  { key: "accountName", label: "Account name", max: 100 },
  { key: "accountNumber", label: "Account number", max: 30 },
  { key: "ifsc", label: "IFSC", placeholder: "HDFC0001234", max: 11 },
];

/** A light check before saving; the server has the final word. */
export const paymentDetailsError = (pay: PayForm): string => {
  const upi = pay.upiId.trim();
  if (upi && !/^[a-zA-Z0-9._-]{2,256}@[a-zA-Z][a-zA-Z0-9.-]{1,63}$/.test(upi)) return "UPI ID looks wrong. It is like name@bank.";
  const ifsc = pay.ifsc.trim();
  if (ifsc && !/^[A-Za-z]{4}0[A-Za-z0-9]{6}$/.test(ifsc)) return "IFSC looks wrong. It is 11 characters, like HDFC0001234.";
  const account = pay.accountNumber.replace(/\s/g, "");
  if (account && !/^\d{6,20}$/.test(account)) return "Account number should be 6 to 20 digits.";
  return "";
};

const readAuthUser = () => {
  try {
    return JSON.parse(localStorage.getItem("authUser") || "null") || {};
  } catch {
    return {};
  }
};

/**
 * Studio settings for customer approval links: lines every page shows, default expiry, and the webhook
 * the studio's own system (the HKV WhatsApp assistant) listens on. Owner edits; managers read.
 */
const OrderApprovalSettingsPage = () => {
  const history = useHistory();
  const role = (readAuthUser() as any)?.users?.role;
  const isOwner = role === "ORGANIZATION_ADMIN";
  const [settings, setSettings] = useState<ApprovalSettings | null>(null);
  const [unavailable, setUnavailable] = useState(false);
  const [error, setError] = useState("");
  const [saved, setSaved] = useState("");
  const [busy, setBusy] = useState(false);
  const [webhookUrl, setWebhookUrl] = useState("");
  const [days, setDays] = useState("14");
  const [preOrderNote, setPreOrderNote] = useState("");
  const [policy, setPolicy] = useState("");
  const [secret, setSecret] = useState("");
  // The new secret stays hidden until the owner taps Show; Copy works either way.
  const [showSecret, setShowSecret] = useState(false);
  const [copied, setCopied] = useState(false);
  const [pay, setPay] = useState<PayForm>(EMPTY_PAY);

  const apply = (s: ApprovalSettings) => {
    setSettings(s);
    setWebhookUrl(s.webhookUrl || "");
    setDays(String(s.defaultExpiryDays || 14));
    setPreOrderNote(s.preOrderNote || "");
    setPolicy((s.policyLines || []).join("\n"));
    if (s.webhookSecret) {
      setSecret(s.webhookSecret);
      setShowSecret(false);
      setCopied(false);
    }
    const p = s.paymentDetails || {};
    setPay({
      upiId: p.upiId || "",
      payeeName: p.payeeName || "",
      bankName: p.bankName || "",
      accountName: p.accountName || "",
      accountNumber: p.accountNumber || "",
      ifsc: p.ifsc || "",
      note: p.note || "",
    });
  };

  const staff = isStudioStaff(role);

  useEffect(() => {
    // Anyone else gets a 403 from the API, which would send them to the 401 page.
    if (!staff) {
      history.replace("/orders");
      return;
    }
    getOrderApprovalSettings()
      .then((res: any) => {
        if (res?.success) apply(res.data);
        else if (res?.code === "FEATURE_DISABLED" || res?.code === "PERMISSION_DENIED") setUnavailable(true);
        else setError(res?.message || "Couldn't load the settings");
      })
      .catch(() => setError("Couldn't load the settings"));
  }, [staff, history]);

  const hostOf = (url: string) => {
    try {
      return new URL(url).host.toLowerCase();
    } catch {
      return "";
    }
  };

  // A new secret (asked for, or from a webhook on another host) stops the assistant accepting events until it has it.
  const confirmNewSecret = () =>
    window.confirm(
      "Your WhatsApp assistant will stop accepting HKP events until you give it the new secret. " +
        "Events wait and retry for about 15 hours. Make a new secret?"
    );

  const copySecret = async () => {
    try {
      await navigator.clipboard.writeText(secret);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1600);
    } catch {
      window.prompt("Copy the webhook secret", secret);
    }
  };

  const save = async (extra: Record<string, unknown> = {}) => {
    const savedHost = hostOf(settings?.webhookUrl || "");
    const nextHost = hostOf(webhookUrl.trim());
    const newHost = !!settings?.hasWebhookSecret && !!nextHost && nextHost !== savedHost;
    if ((extra.rotateSecret || newHost) && !confirmNewSecret()) return;
    const payError = paymentDetailsError(pay);
    if (payError) {
      setSaved("");
      setError(payError);
      return;
    }
    setBusy(true);
    setError("");
    setSaved("");
    try {
      const res: any = await saveOrderApprovalSettings({
        webhookUrl: webhookUrl.trim(),
        defaultExpiryDays: Number(days),
        preOrderNote,
        policyLines: policy.split("\n").map((l) => l.trim()).filter(Boolean),
        paymentDetails: {
          upiId: pay.upiId.trim(),
          payeeName: pay.payeeName.trim(),
          bankName: pay.bankName.trim(),
          accountName: pay.accountName.trim(),
          accountNumber: pay.accountNumber.replace(/\s/g, ""),
          ifsc: pay.ifsc.trim().toUpperCase(),
          note: pay.note.trim(),
        },
        ...extra,
      });
      if (res?.success) {
        apply(res.data);
        setSaved("Saved");
      } else {
        setError(res?.message || "Couldn't save");
      }
    } catch {
      setError("Couldn't save");
    } finally {
      setBusy(false);
    }
  };

  if (!staff) return null;

  return (
    <div className="gi-registry-page order-approval-settings">
      <div className="gi-registry-toolbar">
        <h2>
          <button type="button" className="order-approval-back" aria-label="Back" onClick={() => history.goBack()}>
            ‹
          </button>
          Approval links
        </h2>
      </div>
      {unavailable ? <p className="gi-au-hint">Approval links aren't switched on for this studio yet.</p> : null}
      {error ? (
        <p className="gi-form-banner is-error" role="alert">
          {error}
        </p>
      ) : null}
      {!settings && !unavailable && !error ? <p className="gi-au-hint">Loading…</p> : null}
      {settings ? (
        <div className="geo-card">
          <Label for="oaPre">Pre-order note</Label>
          <Input
            id="oaPre"
            value={preOrderNote}
            maxLength={300}
            disabled={!isOwner}
            placeholder="Woven to order, 2 to 4 months"
            onChange={(e) => setPreOrderNote(e.target.value)}
          />
          <Label for="oaPolicy" className="mt-3">
            Care and policy lines (one per line, up to 8)
          </Label>
          <Input
            id="oaPolicy"
            type="textarea"
            rows={4}
            value={policy}
            disabled={!isOwner}
            placeholder={"Custom weaves are not returnable\nDelivery by courier, insured"}
            onChange={(e) => setPolicy(e.target.value)}
          />
          <Label for="oaDays" className="mt-3">
            Links stay open for (days)
          </Label>
          <Input
            id="oaDays"
            type="number"
            min={1}
            max={settings.maxExpiryDays || 30}
            value={days}
            disabled={!isOwner}
            onChange={(e) => setDays(e.target.value)}
          />
          <Label for="oaHook" className="mt-3">
            Webhook URL (your WhatsApp assistant)
          </Label>
          <Input
            id="oaHook"
            type="url"
            value={webhookUrl}
            disabled={!isOwner}
            placeholder="https://chat.example.com/hooks/hkp-approval"
            onChange={(e) => setWebhookUrl(e.target.value)}
          />
          <p className="gi-au-hint mb-0">
            HKP posts order_approval.viewed, approved, changes_requested, expired, superseded, revoked and
            order_changed (the order changed after the customer approved) here, signed with
            X-HKP-Signature: sha256=HMAC(secret, timestamp + "." + body) and X-HKP-Timestamp.
          </p>
          {secret ? (
            <div className="gi-form-banner is-info mt-2">
              New webhook secret. It is shown only on this page; copy it into your assistant now.
              <div className="order-approval-secret-row">
                <code className="order-approval-secret" aria-label={showSecret ? "Webhook secret" : "Webhook secret, hidden"}>
                  {showSecret ? secret : "•".repeat(16)}
                </code>
                <Button size="sm" className="btn-hastkala-outline" onClick={() => setShowSecret((v) => !v)}>
                  {showSecret ? "Hide" : "Show"}
                </Button>
                <Button size="sm" className="btn-hastkala-outline" onClick={copySecret}>
                  {copied ? "Copied" : "Copy"}
                </Button>
              </div>
            </div>
          ) : settings.hasWebhookSecret ? (
            <p className="gi-au-hint">A webhook secret is set.</p>
          ) : null}
          <h3 className="order-approval-subhead">Payment details for advance</h3>
          <p className="gi-au-hint">
            Shown to the customer on the approval page while the advance is due, with a Pay with UPI button when a
            UPI ID is set. Leave empty to share them yourself.
          </p>
          {PAY_FIELDS.map((f) => (
            <React.Fragment key={f.key}>
              <Label for={`oaPay-${f.key}`} className="mt-2">
                {f.label}
              </Label>
              <Input
                id={`oaPay-${f.key}`}
                value={pay[f.key]}
                maxLength={f.max}
                disabled={!isOwner}
                placeholder={f.placeholder}
                autoComplete="off"
                autoCapitalize={f.key === "ifsc" ? "characters" : f.key === "upiId" ? "none" : undefined}
                inputMode={f.key === "accountNumber" ? "numeric" : undefined}
                onChange={(e) => {
                  const value = e.target.value;
                  setPay((p) => ({ ...p, [f.key]: value }));
                }}
              />
            </React.Fragment>
          ))}
          <Label for="oaPay-note" className="mt-2">
            Note for the customer
          </Label>
          <Input
            id="oaPay-note"
            type="textarea"
            rows={2}
            value={pay.note}
            maxLength={300}
            disabled={!isOwner}
            placeholder="Please add your order number in the payment remark"
            onChange={(e) => {
              const value = e.target.value;
              setPay((p) => ({ ...p, note: value }));
            }}
          />
          {saved ? <p className="gi-au-hint mt-2">{saved}</p> : null}
          {isOwner ? (
            <div className="gi-form-actions">
              {settings.hasWebhookSecret ? (
                <Button className="btn-hastkala-outline" disabled={busy} onClick={() => save({ rotateSecret: true })}>
                  New secret
                </Button>
              ) : null}
              <Button className="ResetBtn" disabled={busy} onClick={() => save()}>
                {busy ? "Saving…" : "Save"}
              </Button>
            </div>
          ) : (
            <p className="gi-au-hint">Only the studio owner can change these.</p>
          )}
        </div>
      ) : null}
    </div>
  );
};

export default OrderApprovalSettingsPage;
